/*
 * DRAG-TO-RESIZE FOR THE AI RAIL — one implementation, every shell.
 *
 * Two shells render this rail: the shared `<vitesse-shell>` and app-ui's own `<app-shell>`. The
 * resize behaviour is not two behaviours, so it is not written twice — the pointer maths, the
 * bounds, the persistence key and the transition-suppression flag all live here, and a fix lands
 * everywhere at once.
 *
 * A Lit ReactiveController rather than a mixin or a base class: the two hosts do not share an
 * ancestor and should not be forced to, and a controller composes into whatever a host already is.
 */
import { css } from 'lit';
/** Narrow enough to still be a rail; wide enough for a pipeline view. Past the maximum it stops
 *  being a rail and the main content becomes unusable, which is not a width anyone wants to reach
 *  by dragging slightly too far. */
export const RAIL_MIN_W = 300;
export const RAIL_MAX_W = 900;
export const RAIL_DEFAULT_W = 360;
/** The assistant rail's own remembered width. Exported because the panel's leftmost column adopts
 *  it: a stack of one column IS the rail, and a reader's dragged width must survive the change. */
export const RAIL_STORAGE_KEY = 'vitesse.ai.railWidth';
const STORAGE_KEY = RAIL_STORAGE_KEY;
export const clampRailWidth = (n, max = RAIL_MAX_W, min = RAIL_MIN_W) => Math.min(max, Math.max(min, Math.round(n)));
/**
 * Restore the persisted width.
 *
 * A width is a PREFERENCE, and re-dragging it after every reload is the layout-shift complaint in a
 * different costume. Anything unparseable or out of range falls back to the default, so a corrupted
 * entry cannot leave the rail at 4px with no way to grab it.
 */
export function readRailWidth(key = STORAGE_KEY, max = RAIL_MAX_W, min = RAIL_MIN_W, fallback = RAIL_DEFAULT_W) {
    try {
        const raw = Number(localStorage.getItem(key));
        return Number.isFinite(raw) && raw > 0 ? clampRailWidth(raw, max, min) : clampRailWidth(fallback, max, min);
    }
    catch {
        return clampRailWidth(fallback, max, min); // private mode / storage disabled
    }
}
/*
 * THE WALL IS THE HANDLE — one drawing of it, every resizable edge.
 *
 * Not a separate grip floating beside the border: the surface's own left wall IS what you grab. So
 * the element draws the divider (its host has no border of its own) and that same 1px line thickens
 * and turns accent-blue under the pointer. Anything offset from the wall reads as a second,
 * unrelated affordance and misses the thing it is meant to be attached to.
 *
 * Shared by the rail and by every column of a stack, because "drag this edge" is one control. A
 * second copy is how the two come to feel subtly different — a wider hit area here, a different
 * hover colour there — on what the reader experiences as the same gesture.
 */
export const resizeWallCss = css `
  .wall {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    /* The hit area is wider than the line it draws — a 1px target is a target you miss — but it is
       CENTRED on the wall, so the pointer grabs exactly what it sees. */
    width: 11px;
    transform: translateX(-5px);
    cursor: col-resize;
    /*
     * ABOVE THE SLOTTED CONTENT, not merely above the host's own background. Slotted content
     * flattens into the host's stacking context, so a panel's own layers (a frosted sticky header)
     * compete with this directly and would cover the divider for exactly their own height. The wall
     * is the edge, and nothing inside should paint over it.
     */
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: none;
  }
  .wall::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 1px;
    height: 100%;
    background: var(--border-color);
    transition:
      width 0.12s ease,
      background 0.12s ease;
  }
  .wall:hover::after,
  :host([resizing]) .wall::after {
    width: 3px;
    background: var(--blue, var(--accent));
  }
`;
export class RailResizeController {
    constructor(host, bounds = {}) {
        this.host = host;
        this.bounds = bounds;
        /** Current width in px. Read by the host's render. */
        this.width = readRailWidth();
        /** True only during a drag. Hosts use it to suppress the rail's width transition — a transition on
         *  a pointermove handler makes the edge lag the cursor, which reads as a broken drag rather than a
         *  smoothed one. */
        this.resizing = false;
        /**
         * Begin a drag.
         *
         * POINTER events, not mouse: a trackpad, a pen and a touchscreen all work from one path. The
         * pointer is CAPTURED so a fast drag that outruns the narrow grip keeps resizing instead of
         * dropping the gesture the moment the cursor leaves the element.
         *
         * The rail sits on the RIGHT, so dragging LEFT must make it wider — the delta is inverted relative
         * to the obvious reading, and getting that backwards is the classic bug in this control.
         */
        this.start = (e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = this.width;
            const grip = e.currentTarget;
            grip.setPointerCapture(e.pointerId);
            this.resizing = true;
            this.bounds.onDragStart?.();
            this.host.requestUpdate();
            const move = (ev) => {
                const want = clampRailWidth(startW + (startX - ev.clientX), this.max, this.min);
                // On a SHARED wall the neighbour is asked for the difference first, and only what it grants is
                // taken: at its floor it gives less and this column grows by less, so a divider stops moving
                // rather than crushing the panel on the other side of it.
                const delta = this.bounds.share ? this.bounds.share(want - startW) : want - startW;
                this.width = clampRailWidth(startW + delta, this.max, this.min);
                this.bounds.onChange?.(this.width);
                this.host.requestUpdate();
            };
            const done = () => {
                this.resizing = false;
                /*
                 * WRITTEN DOWN FIRST, and that order is the whole of a bug that undid every shared drag.
                 *
                 * Written on RELEASE rather than per move: a pointermove handler runs at input frequency and
                 * localStorage is synchronous, so persisting per frame would put a blocking write on the drag.
                 *
                 * But it has to happen BEFORE the neighbour is told. `onDragEnd` makes the other side of the
                 * boundary write ITS width, which announces, which makes a host recompute the layout from what
                 * every column was DRAGGED to — read from here. With the write after, this column's dragged
                 * width was not yet down, the host read the width from before the gesture, and put the column
                 * back: the divider moved under the pointer and snapped home on release. Observed exactly that
                 * way, and only once columns had room to move at all.
                 */
                try {
                    localStorage.setItem(this.key, String(this.width));
                }
                catch {
                    /* quota / private mode — the width simply does not persist */
                }
                this.bounds.onDragEnd?.();
                grip.removeEventListener('pointermove', move);
                grip.removeEventListener('pointerup', done);
                grip.removeEventListener('pointercancel', done);
                this.host.requestUpdate();
                /*
                 * AND ONE LAST ANNOUNCEMENT, after the drag is over and the width is written down.
                 *
                 * A host that corrects the layout has to keep its hands off mid-gesture, so every `onChange`
                 * during the drag was ignored — and then nothing told it the gesture had ended. The columns
                 * stayed at whatever the drag left them summing to, past the cap, relying on the flex row to
                 * squeeze them. This is the edge that says "now".
                 */
                this.bounds.onChange?.(this.width);
            };
            grip.addEventListener('pointermove', move);
            grip.addEventListener('pointerup', done);
            grip.addEventListener('pointercancel', done);
        };
        host.addController(this);
    }
    get max() {
        return this.bounds.maxWidth?.() ?? RAIL_MAX_W;
    }
    get min() {
        return this.bounds.minWidth?.() ?? RAIL_MIN_W;
    }
    get key() {
        return this.bounds.storageKey?.() ?? STORAGE_KEY;
    }
    hostConnected() {
        // The bounds are the host's properties, which exist only now — the field initialiser above ran
        // with the defaults, so the remembered width is read again against this one's own key and caps.
        this.restore();
    }
    /** Re-read the remembered width. Called on connect, and again by a host whose key changed — a
     *  column that becomes the second column reads the second column's width, not the first's. */
    restore() {
        this.width = readRailWidth(this.key, this.max, this.min, this.bounds.defaultWidth?.() ?? RAIL_DEFAULT_W);
        this.bounds.onChange?.(this.width);
    }
    /** What this was last DRAGGED to, read back from storage — the width it wants when there is room
     *  for it, as opposed to the width it is currently squeezed into. */
    get desired() {
        return readRailWidth(this.key, this.max, this.min, this.bounds.defaultWidth?.() ?? RAIL_DEFAULT_W);
    }
    /**
     * Set the width from OUTSIDE a drag — a host redistributing across a shared wall. Persisted like a
     * drag, because it is one: somebody moved a divider and this is the other half of that move.
     *
     * WRITTEN DOWN BEFORE IT IS ANNOUNCED, for the same reason `done` writes before it calls
     * `onDragEnd`: announcing is what makes a host recompute the layout, and the host recomputes from
     * what each column was DRAGGED to — which it reads from here. With the write after the
     * announcement the host read the width from before this call, decided this column was in the
     * wrong place, moved it back, and the write then recorded the correction instead of the move.
     * Seen as a divider that moved under the pointer and sprang home the moment it was released.
     */
    setWidth(px, persist = true) {
        this.width = clampRailWidth(px, this.max, this.min);
        if (persist) {
            try {
                localStorage.setItem(this.key, String(this.width));
            }
            catch {
                /* quota / private mode — the width simply does not persist */
            }
        }
        this.bounds.onChange?.(this.width);
        this.host.requestUpdate();
    }
}
/** Next pane state when the AI rail is toggled. */
export function onToggleAi(s) {
    const aiOpen = !s.aiOpen;
    // Closing the rail leaves the tree exactly as the user last had it — restoring it would be a
    // second unrequested move, and the layout would jump twice for one click.
    return { aiOpen, treeOpen: aiOpen ? false : s.treeOpen };
}
/** Next pane state when the tree is toggled. Never touches the rail — see the asymmetry above. */
export function onToggleTree(s) {
    return { ...s, treeOpen: !s.treeOpen };
}
/*
 * IS THE ASSISTANT WORKING? — counted, not latched.
 *
 * The rail collapses to zero width rather than unmounting, so work continues while it is shut and
 * the header has to say so. What reports it is each open SURFACE, and the stack draws up to three
 * of them at once — so a single boolean assigned from every announcement is whatever the last
 * surface to speak said. Two ways that went wrong, both seen: a context column finishing cleared
 * the indicator while the column beside it was still working, and a column CLOSED mid-turn never
 * retracted its claim at all, leaving the header lit for the rest of the session with nothing
 * running behind it.
 *
 * A set of reporters answers the question the header actually asks — is ANY of them working — and
 * it is self-healing: a reporter that goes quiet is one id removed, never a flag that can stick.
 *
 * Exported as one object rather than written into each shell, for the same reason the pane rules
 * above are: two shells, one product decision.
 */
export class AiBusyTally {
    constructor() {
        this.busy = new Set();
    }
    /** Fold one announcement in. Returns true when the ANSWER changed, so a shell can skip a render. */
    note(id, busy) {
        const before = this.busy.size > 0;
        if (busy)
            this.busy.add(id);
        else
            this.busy.delete(id);
        return this.busy.size > 0 !== before;
    }
    /** Is anything working right now? */
    get working() {
        return this.busy.size > 0;
    }
}
//# sourceMappingURL=rail-resize.js.map